import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Scale } from 'lucide-react'
import { Bidi, Button, Card, CardBody, CardHeader, Input, useLocale } from '@hk/ui'
import {
  ApiError, approveAdjustment, declineAdjustment, fetchAdjustments, formatDateTime,
  formatQuantity, nameIn, proposeAdjustment, translateError,
  type StockAdjustmentRow, type StockLevelView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

/* ============================================================================
   **CQ-42 — A CORRECTION WAITING FOR A SECOND PERSON.** Sprint 82.

   Your answer, in your own words: *"Agreed, all adjustment must be approved by supervisor,
   and supervisor should receive a notification for it."*

   ── WHY IT IS ON W-100 AND NOT A SCREEN OF ITS OWN ──────────────────────────────────
   This is where the store keeper and the housekeeping manager already are, and the two
   grants involved are already what this screen is drawn for. A new screen would have been
   a new code in the Screen Inventory for a card with at most a handful of rows on it, and
   one more place a hotel has to remember to look.

   ── NOTHING MOVES UNTIL SOMEBODY ELSE AGREES ────────────────────────────────────────
   The card says so where it is raised, because a store keeper who writes off four torn
   towels and watches the shelf figure not move will otherwise conclude the product lost
   his correction. W-106 has behaved this way since Sprint 24; this makes the typed
   correction behave like the counted one.

   ── AND THE BUTTONS ARE NOT DRAWN WHERE THEY WOULD BE REFUSED ───────────────────────
   `mayDecide` comes from the server and is false on your own proposal. A screen that drew
   Approve and then refused the press would teach its reader that the product is
   unreliable rather than that the separation rule exists — so instead it says, in words,
   that somebody else has to look at this one.
   ========================================================================= */

export function AdjustmentsCard(
  { propertyId, levels }: { propertyId: string; levels: readonly StockLevelView[] },
) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const client = useQueryClient()

  const queue = useQuery({
    queryKey: ['stock-adjustments', propertyId],
    queryFn: ({ signal }) => fetchAdjustments(propertyId, { signal }),
    retry: false,
  })

  const [raising, setRaising] = useState(false)
  const [shelf, setShelf] = useState('')
  const [amount, setAmount] = useState('')
  const [why, setWhy] = useState('')
  const [decliningId, setDecliningId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [failed, setFailed] = useState<unknown>(null)

  const refresh = async () => {
    setFailed(null)
    await client.invalidateQueries({ queryKey: ['stock-adjustments', propertyId] })
    /* **AND THE FIGURES THIS SCREEN DRAWS**, because approving one moves a shelf. A card
       that refreshed only itself would sit above a table still showing the old number. */
    await client.invalidateQueries({ queryKey: ['stock-levels', propertyId] })
    await client.invalidateQueries({ queryKey: ['stock-on-hand', propertyId] })
  }

  const propose = useMutation({
    mutationFn: () => {
      const row = (levels ?? []).find(
        (l) => `${l.stockItemId}|${l.stockLocationId}` === shelf)
      return proposeAdjustment(propertyId, {
        stockItemId: row!.stockItemId,
        stockLocationId: row!.stockLocationId,
        quantity: Number(amount),
        reason: why.trim(),
      })
    },
    onSuccess: async () => {
      setRaising(false); setShelf(''); setAmount(''); setWhy('')
      await refresh()
    },
    onError: setFailed,
  })

  const approve = useMutation({
    mutationFn: (id: string) => approveAdjustment(propertyId, id),
    onSuccess: refresh,
    onError: setFailed,
  })

  const decline = useMutation({
    mutationFn: (id: string) => declineAdjustment(propertyId, id, note.trim()),
    onSuccess: async () => { setDecliningId(null); setNote(''); await refresh() },
    onError: setFailed,
  })

  /* **A READER WITHOUT `stock.adjust` SEES NOTHING AT ALL**, rather than a refusal. W-100
     opens for anybody with stock rights, and a card shouting "not allowed" at a store
     keeper doing his ordinary morning would be the screen accusing him of something. */
  if (queue.error instanceof ApiError && queue.error.code === 'permission.denied') return null
  if (!queue.data) return null

  const { waiting, decided } = queue.data
  const busy = propose.isPending || approve.isPending || decline.isPending

  const shelves = (levels ?? []).map((l) => ({
    key: `${l.stockItemId}|${l.stockLocationId}`,
    label: `${nameIn(locale, l.itemName, l.itemNameAr)} · `
      + `${nameIn(locale, l.locationName, l.locationNameAr)}`,
  }))

  return (
    <Card>
      <CardHeader
        title={t.adjTitle}
        subtitle={t.adjSubtitle}
        icon={<Scale className="size-4" />}
        actions={
          !raising ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setRaising(true)}>
              {t.adjRaise}
            </Button>
          ) : null
        }
      />
      <CardBody className="space-y-3">

        {raising && (
          <div className="space-y-2 rounded-hk border border-border bg-muted/30 p-3">
            {/* **THE SENTENCE THAT PREVENTS THE COMPLAINT**, above the fields rather than
                after the press. */}
            <p className="text-[12px] text-muted-foreground">{t.adjNothingMoves}</p>

            <select
              aria-label={t.adjWhat}
              className="h-9 w-full rounded-hk border border-border bg-background px-2 text-[13px]"
              value={shelf}
              onChange={(e) => setShelf(e.target.value)}
            >
              <option value="">{t.adjPickShelf}</option>
              {shelves.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>

            <div className="flex flex-wrap items-center gap-2">
              <Input
                aria-label={t.adjBy}
                className="w-28 tnum"
                inputMode="decimal"
                placeholder={t.adjByHint}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <Input
                aria-label={t.adjWhy}
                className="min-w-[14rem] flex-1"
                placeholder={t.adjWhyHint}
                value={why}
                onChange={(e) => setWhy(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={busy || !shelf || !amount.trim() || !why.trim()}
                onClick={() => propose.mutate()}
              >
                {t.adjSend}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { setRaising(false); setFailed(null) }}
              >
                {t.cancel}
              </Button>
            </div>
          </div>
        )}

        {failed ? (
          <p role="alert" className="text-[12px] text-[#b91c1c] dark:text-[#fca5a5]">
            {String(translateError(failed, locale).message)}
          </p>
        ) : null}

        {waiting.length === 0 && !raising && (
          <p className="text-[13px] text-muted-foreground">{t.adjNoneWaiting}</p>
        )}

        {waiting.map((row) => (
          <div key={row.stockAdjustmentId} className="rounded-hk border border-border p-3">
            <Line row={row} locale={locale} t={t} />

            {row.mayDecide ? (
              decliningId === row.stockAdjustmentId ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input
                    aria-label={t.adjWhyNot}
                    className="min-w-[16rem] flex-1"
                    placeholder={t.adjWhyNotHint}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <Button
                    type="button" size="sm" variant="destructive"
                    disabled={busy || !note.trim()}
                    onClick={() => decline.mutate(row.stockAdjustmentId)}
                  >
                    {t.adjDecline}
                  </Button>
                  <Button
                    type="button" size="sm" variant="ghost"
                    onClick={() => { setDecliningId(null); setNote('') }}
                  >
                    {t.cancel}
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    type="button" size="sm" disabled={busy}
                    onClick={() => approve.mutate(row.stockAdjustmentId)}
                  >
                    {t.adjApprove}
                  </Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => { setDecliningId(row.stockAdjustmentId); setNote('') }}
                  >
                    {t.adjDecline}
                  </Button>
                </div>
              )
            ) : (
              /* **IT SAYS WHY THERE ARE NO BUTTONS.** A disabled control must leave
                 somebody something to do, and here the thing to do is ask a colleague. */
              <p className="mt-2 text-[12px] text-muted-foreground">{t.adjYoursToWaitFor}</p>
            )}
          </div>
        ))}

        {decided.length > 0 && (
          <div className="space-y-1.5 border-t border-border pt-2">
            <p className="text-[11.5px] uppercase tracking-wide text-muted-foreground">
              {t.adjRecent}
            </p>
            {decided.map((row) => (
              <p key={row.stockAdjustmentId} className="text-[12px] text-muted-foreground">
                <Bidi>
                  {fill(t.adjDecidedLine, {
                    what: nameIn(locale, row.itemName, row.itemNameAr),
                    by: formatQuantity(row.quantity, locale),
                    state: t.adjStates[row.state],
                    who: row.decidedBy ?? '',
                  })}
                </Bidi>
                {row.decisionNote ? <> — <Bidi>{row.decisionNote}</Bidi></> : null}
              </p>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

function Line(
  { row, locale, t }:
  { row: StockAdjustmentRow; locale: 'en' | 'ar'; t: typeof STRINGS['en'] },
) {
  return (
    <>
      <p className="text-[13px]">
        <Bidi>
          <b>{nameIn(locale, row.itemName, row.itemNameAr)}</b>
          {' · '}
          {nameIn(locale, row.locationName, row.locationNameAr)}
        </Bidi>
      </p>
      {/* **THE FIGURE AND WHAT IT WOULD BE TAKEN FROM**, on one line. "Write off four" is
          unanswerable without the second number, and the second person is being asked
          exactly that question. */}
      <p className="tnum text-[13px] font-medium">
        {fill(t.adjMovesBy, {
          by: formatQuantity(row.quantity, locale),
          from: formatQuantity(row.onHandNow, locale),
          to: formatQuantity(row.onHandNow + row.quantity, locale),
        })}
      </p>
      <p className="text-[12px] text-muted-foreground">
        <Bidi>{row.reason}</Bidi>
      </p>
      <p className="text-[11.5px] text-muted-foreground">
        {fill(t.adjRaisedBy, {
          who: nameIn(locale, row.raisedBy, row.raisedByAr),
          when: formatDateTime(new Date(row.raisedAtUtc), locale),
        })}
      </p>
    </>
  )
}
